import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Circle,
  ListTodo,
  Loader2,
  RotateCcw,
  SkipForward,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import {
  STRATEGY_SCORE_LABELS,
  isOpenTaskStatus,
  taskStatusUpdatePatch,
  type NetworkingTaskRow,
  type NetworkingTaskStatus,
} from '../../lib/networkingTasks';

export const TRANSCRIPT_REVIEW_STEPS = [
  'Skim the summary — what happened, who said what matters (do not re-read the whole meeting).',
  'Narrative arc extract (tight): at most 0–2 ideas. Title + short abstract only. Prefer none unless something clearly maps to an active arc.',
  'Via MCP/Chat: engine_propose_transcript_arc_signals — ghosts on /narrative-arcs with reference back to this transcript.',
  'Other follow-ups (CRM, tasks) separately — do not dump long signal essays onto the arc board.',
  'Mark review done when you have captured what you need (or skip if nothing for the arcs).',
] as const;

function formatWhen(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

interface TranscriptReviewTaskPanelProps {
  transcriptId: string;
  /** Slim bar for chat modal vs full card on detail page. */
  compact?: boolean;
  className?: string;
}

const TranscriptReviewTaskPanel: React.FC<TranscriptReviewTaskPanelProps> = ({
  transcriptId,
  compact = false,
  className = '',
}) => {
  const [task, setTask] = useState<NetworkingTaskRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [stepsOpen, setStepsOpen] = useState(!compact);
  const [strategyScore, setStrategyScore] = useState<number | ''>('');

  const loadTask = useCallback(async () => {
    if (!transcriptId.trim()) {
      setTask(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .eq('transcript_id', transcriptId)
        .eq('task_type', 'transcript_review')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      const row = (data ?? null) as NetworkingTaskRow | null;
      setTask(row);
      setStrategyScore(
        row?.strategy_score != null && row.strategy_score >= 1 && row.strategy_score <= 5
          ? row.strategy_score
          : '',
      );
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to load review task');
      setTask(null);
    } finally {
      setLoading(false);
    }
  }, [transcriptId]);

  useEffect(() => {
    void loadTask();
  }, [loadTask]);

  const updateTask = async (status: NetworkingTaskStatus, extra?: Record<string, unknown>) => {
    if (!task) return;
    setBusy(true);
    try {
      const patch: Record<string, unknown> = {
        ...taskStatusUpdatePatch(status),
        updated_at: new Date().toISOString(),
        ...extra,
      };
      const { data, error } = await supabase
        .from('tasks')
        .update(patch)
        .eq('id', task.id)
        .select('*')
        .single();
      if (error) throw error;
      setTask(data as NetworkingTaskRow);
      if (status === 'done') {
        toast.success('Transcript review marked done');
      } else if (status === 'skipped') {
        toast.success('Review skipped');
      } else if (status === 'pending') {
        toast.success('Review reopened');
      } else if (status === 'in_progress') {
        toast.success('Review in progress');
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const saveStrategyScore = async (score: number | null) => {
    if (!task) return;
    setBusy(true);
    try {
      const { data, error } = await supabase
        .from('tasks')
        .update({ strategy_score: score, updated_at: new Date().toISOString() })
        .eq('id', task.id)
        .select('*')
        .single();
      if (error) throw error;
      setTask(data as NetworkingTaskRow);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to save strategy score');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div
        className={`flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 ${className}`}
        aria-busy="true"
      >
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading review task…
      </div>
    );
  }

  if (!task) return null;

  const isOpen = isOpenTaskStatus(task.status);
  const completedWhen = formatWhen(task.completed_at);

  if (!isOpen) {
    return (
      <div
        className={`rounded-xl border border-emerald-200 dark:border-emerald-800/60 bg-emerald-50/80 dark:bg-emerald-950/30 px-4 py-3 ${className}`}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-start gap-2 min-w-0">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-emerald-900 dark:text-emerald-100">
                Transcript review {task.status === 'skipped' ? 'skipped' : 'complete'}
              </p>
              {completedWhen ? (
                <p className="text-xs text-emerald-800/80 dark:text-emerald-200/80 mt-0.5">
                  {completedWhen}
                </p>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={() => void updateTask('pending')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-emerald-300 dark:border-emerald-700 text-emerald-800 dark:text-emerald-200 hover:bg-emerald-100/80 dark:hover:bg-emerald-900/40 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
            Reopen review
          </button>
        </div>
      </div>
    );
  }

  const actionButtons = (
    <div className="flex flex-wrap items-center gap-2">
      {task.status === 'pending' ? (
        <button
          type="button"
          disabled={busy}
          onClick={() => void updateTask('in_progress')}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-teal-300 dark:border-teal-700 text-teal-800 dark:text-teal-200 hover:bg-teal-100/80 dark:hover:bg-teal-900/40 disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Circle className="w-3.5 h-3.5" />}
          Start review
        </button>
      ) : null}
      <button
        type="button"
        disabled={busy}
        onClick={() => void updateTask('done', strategyScore === '' ? {} : { strategy_score: strategyScore })}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
        Mark review done
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={() => void updateTask('skipped')}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
        title="Nothing actionable from this meeting"
      >
        <SkipForward className="w-3.5 h-3.5" />
        Skip
      </button>
      <Link
        to="/tasks"
        className="inline-flex items-center px-2 py-1.5 text-xs font-medium text-teal-700 dark:text-teal-300 hover:underline"
      >
        All tasks
      </Link>
    </div>
  );

  if (compact) {
    return (
      <div
        className={`shrink-0 border-b border-teal-200 dark:border-teal-900/50 bg-teal-50/90 dark:bg-teal-950/40 px-4 py-2.5 ${className}`}
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <ListTodo className="w-4 h-4 text-teal-600 dark:text-teal-400 shrink-0" />
            <span className="text-xs font-medium text-teal-900 dark:text-teal-100 truncate">
              Review this transcript
            </span>
            <span className="text-[10px] uppercase tracking-wide text-teal-700/80 dark:text-teal-300/80">
              {task.status.replace('_', ' ')}
            </span>
          </div>
          {actionButtons}
        </div>
        <button
          type="button"
          onClick={() => setStepsOpen((v) => !v)}
          className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-teal-800 dark:text-teal-200 hover:underline"
        >
          {stepsOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          What to do
        </button>
        {stepsOpen ? (
          <ol className="mt-2 space-y-1.5 list-decimal list-inside text-[11px] text-teal-900/90 dark:text-teal-100/90 leading-snug">
            {TRANSCRIPT_REVIEW_STEPS.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        ) : null}
      </div>
    );
  }

  return (
    <section
      className={`rounded-xl border border-teal-200 dark:border-teal-800/60 bg-gradient-to-br from-teal-50/90 to-amber-50/40 dark:from-teal-950/50 dark:to-gray-900/40 p-5 ${className}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-teal-900 dark:text-teal-100 flex items-center gap-2">
            <ListTodo className="w-4 h-4 text-teal-600 dark:text-teal-400 shrink-0" />
            Transcript review
            <span className="font-normal text-xs text-teal-700/90 dark:text-teal-300/90 capitalize">
              · {task.status.replace('_', ' ')}
            </span>
          </h2>
          {task.notes?.trim() ? (
            <p className="mt-1 text-sm text-teal-900/85 dark:text-teal-100/85">{task.notes.trim()}</p>
          ) : null}
        </div>
        {actionButtons}
      </div>

      <div className="mt-4">
        <button
          type="button"
          onClick={() => setStepsOpen((v) => !v)}
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-teal-800 dark:text-teal-200 hover:underline"
        >
          {stepsOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          Review checklist
        </button>
        {stepsOpen ? (
          <ol className="mt-3 space-y-2 list-decimal list-inside text-sm text-teal-950/90 dark:text-teal-50/90 leading-relaxed">
            {TRANSCRIPT_REVIEW_STEPS.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        ) : null}
      </div>

      <div className="mt-4 pt-4 border-t border-teal-200/80 dark:border-teal-800/50">
        <label
          htmlFor={`strategy-score-${task.id}`}
          className="block text-xs font-medium text-teal-900 dark:text-teal-100 mb-1"
        >
          Strategy fit <span className="font-normal text-teal-700/80 dark:text-teal-300/70">(optional)</span>
        </label>
        <select
          id={`strategy-score-${task.id}`}
          value={strategyScore}
          disabled={busy}
          onChange={(e) => {
            const raw = e.target.value;
            const next = raw ? Number(raw) : null;
            setStrategyScore(raw ? Number(raw) : '');
            void saveStrategyScore(next);
          }}
          className="text-sm rounded-lg border border-teal-200 dark:border-teal-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-1.5 disabled:opacity-50 max-w-xs"
        >
          <option value="">Not scored yet</option>
          {[1, 2, 3, 4, 5].map((score) => (
            <option key={score} value={score}>
              {score} — {STRATEGY_SCORE_LABELS[score]}
            </option>
          ))}
        </select>
      </div>
    </section>
  );
};

export default TranscriptReviewTaskPanel;
